/**
 * Curated screenshots used by the public homepage (run.cards).
 *
 * Sourced from the marketing_screenshots MongoDB collection — all are stored
 * in the public-read GCS bucket `storage.googleapis.com/runcards/marketing-screenshots/`,
 * so the public site can <img src> them directly with no auth.
 *
 * Why hard-coded instead of fetched: the homepage shouldn't depend on the
 * admin API at page-load time, and these don't change often. When the marketing
 * team adds a new bank screenshot they want featured, drop its URL in here.
 *
 * Each entry has:
 *   - id:       stable key for React lists
 *   - label:    short human-readable name
 *   - url:      public GCS URL
 *   - kind:     'map' | 'card' | 'magazine' | 'list' | 'profile'
 *
 * Pulled from runcards.marketing_screenshots, 2026-06-01.
 */

const BASE = "https://storage.googleapis.com/runcards/marketing-screenshots";

export const HOMEPAGE_SCREENSHOTS = {
  // ─── Map views (used to show "runs anywhere") ──────────────────────
  worldwideMap: {
    id: "worldwide-map",
    label: "Runs worldwide",
    url: `${BASE}/runcards@gmail.com/ad34ed50-cd35-40ee-9a7e-10c50feedafa.png`,
    kind: "map",
  },
  newYorkMap: {
    id: "new-york-map",
    label: "Runs in New York",
    url: `${BASE}/runcards@gmail.com/8f5d12df-039f-43a7-a3f6-8616659e0a4c.png`,
    kind: "map",
  },
  austinMap: {
    id: "austin-map",
    label: "Runs in Austin",
    url: `${BASE}/runcards@gmail.com/68cc671a-2259-46eb-bd1d-b83abaa5166c.png`,
    kind: "map",
  },
  bocaMap: {
    id: "boca-map",
    label: "Boca Run Club — weekly runs",
    url: `${BASE}/runcards@gmail.com/7b80c792-67ff-46eb-8966-aee604188c9d.png`,
    kind: "map",
  },

  // ─── Lists / Discover / Schedule ──────────────────────────────────
  exploreNewYork: {
    id: "explore-ny",
    label: "Explore — New York",
    url: `${BASE}/runcards@gmail.com/67fe393f-b198-45cc-8050-b7a42a305ccd.png`,
    kind: "list",
  },
  exploreRunsNewYork: {
    id: "explore-runs-ny",
    label: "Explore runs — New York",
    url: `${BASE}/runcards@gmail.com/f5bbf6b6-8a05-4dbc-9220-11491606dc01.png`,
    kind: "list",
  },
  schedule: {
    id: "schedule",
    label: "Your weekly schedule",
    url: `${BASE}/runcards@gmail.com/22331736-8ce2-44a9-8756-f9df2bb97ca3.png`,
    kind: "list",
  },

  // ─── Magazine views (visually rich, great for "club spotlight") ──
  orchardClub: {
    id: "orchard-club",
    label: "Orchard Street Runners",
    url: `${BASE}/runcards@gmail.com/f14b1ee9-97fa-42a6-9985-3ffb3f38c686.png`,
    kind: "magazine",
  },
  orchardRun: {
    id: "orchard-run",
    label: "Orchard Street Runners — Monday Run",
    url: `${BASE}/runcards@gmail.com/3592ad66-5cc5-4138-92e1-efa05a2ec1d2.png`,
    kind: "magazine",
  },
  midnightCity: {
    id: "midnight-city",
    label: "Midnight City Runners",
    url: `${BASE}/runcards@gmail.com/8c4e335c-565e-4e53-9b90-1ea300f4e383.png`,
    kind: "magazine",
  },
  runclub86Magazine: {
    id: "runclub-86-magazine",
    label: "86 Run Club — magazine view",
    url: `${BASE}/runcards@gmail.com/91a90253-d678-4370-ab78-070ae754efd9.png`,
    kind: "magazine",
  },

  // ─── Run cards (single meeting detail) ────────────────────────────
  weRunUptown: {
    id: "we-run-uptown",
    label: "We Run Uptown — Monday",
    url: `${BASE}/runcards@gmail.com/cbeef7b4-e173-4ac9-b504-47a020c98134.png`,
    kind: "card",
  },
  runclub86Meeting: {
    id: "runclub-86-meeting",
    label: "86 Run Club — meeting",
    url: `${BASE}/runcards@gmail.com/8c596821-6330-4695-bb63-d0079c2ded7b.png`,
    kind: "card",
  },
  runDetails: {
    id: "run-details",
    label: "A run, in detail",
    url: `${BASE}/runcards@gmail.com/c2749a86-e114-4fb9-8e59-44b4870dcba3.png`,
    kind: "card",
  },
  humansOfKitchen: {
    id: "humans-of-kitchen",
    label: "Humans of the Kitchen — share",
    url: `${BASE}/runcards@gmail.com/bc128d91-629b-48a9-b2c1-6603a11ccc5c.png`,
    kind: "card",
  },

  // ─── Profile ──────────────────────────────────────────────────────
  runcardMenu: {
    id: "runcard-menu",
    label: "Your runcard",
    url: `${BASE}/runcards@gmail.com/aecfbeb1-fc63-454f-8baa-66495fc28d11.png`,
    kind: "profile",
  },
};

/** Flat list — used by gallery marquees that want every screenshot. */
export const HOMEPAGE_SCREENSHOTS_LIST = Object.values(HOMEPAGE_SCREENSHOTS);

/** Helper: pick all screenshots of a given kind. */
export const screenshotsByKind = (kind) =>
  HOMEPAGE_SCREENSHOTS_LIST.filter((s) => s.kind === kind);
